import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import PricingPlanCardLanding from "../PricingPlanCardLanding";

// Numbers MUST match the canonical plans in datanodes-backend
// `billing/management/commands/plans.json` — those are the rate-limits
// the gateway actually enforces. See `soul/plans-self-heal-via-plans-json`
// in datanodes-memory.
const plans = [
  {
    name: "FREE",
    planName: "Free",
    subtitle: "Our Free plan. No commitment",
    price: "$0",
    priceSuffix: "per Month",
    features: [
      "25 Req/sec (RPS)",
      "500K Monthly Requests",
      "100K Archive Requests",
      "Community Support",
      "Access to all nodes",
    ],
    cta: "Choose this plan",
    elevated: false,
  },
  {
    name: "BUILDER FLEET",
    planName: "Builder Fleet",
    subtitle: "$0.83 per 1M requests",
    price: "$25",
    priceSuffix: "per Month",
    features: [
      "75 Req/sec (RPS)",
      "30M Monthly Requests",
      "10M Archive Requests",
      "Dedicated Support",
      "Access to all nodes",
    ],
    cta: "Choose this plan",
    elevated: false,
  },
  {
    name: "GROWTH FLEET",
    planName: "Growth Fleet",
    subtitle: "$0.66 per 1M requests",
    price: "$79",
    priceSuffix: "per Month",
    features: [
      "175 Req/sec (RPS)",
      "120M Monthly Requests",
      "40M Archive Requests",
      "Overage Billing Available",
      "Priority Support",
    ],
    cta: "Choose this plan",
    elevated: true,
  },
  {
    name: "PRO FLEET",
    planName: "Pro Fleet",
    subtitle: "$0.50 per 1M requests",
    price: "$199",
    priceSuffix: "per Month",
    features: [
      "300 Req/sec (RPS)",
      "400M Monthly Requests",
      "120M Archive Requests",
      "Overage Billing Available",
      "Priority Support",
    ],
    cta: "Choose this plan",
    elevated: false,
  },
  {
    name: "CUSTOM",
    planName: "Custom",
    subtitle: "",
    price: "Contact us",
    priceSuffix: "",
    features: [
      "Up to 10,000 RPS",
      "Custom Monthly Requests",
      "Custom Archive Volume",
      "Priority Support",
      "Custom Node Selection",
    ],
    cta: "Get in Touch",
    elevated: false,
  },
];

const PricingSection = () => {
  const navigate = useNavigate();

  const handlePickPlan = (planName) => {
    // Custom plan (and future contact-only tiers) skip signup — open
    // mailto directly. Backend rejects POST subscriptions for these plans
    // with 400 anyway; this is the friendly UI path.
    if (planName === "Custom") {
      window.location.href =
        `mailto:nodes@nodefleet.net?subject=${encodeURIComponent(
          "Inquiry about Custom plan",
        )}&body=${encodeURIComponent(
          "Hi,\n\nI'd like to discuss a Custom plan for my organization.\n\n",
        )}`;
      return;
    }
    if (planName) {
      // Read by Login.jsx + Signup.jsx after authentication. Cleared
      // once we hand off to Stripe (or skip for the free plan).
      localStorage.setItem("pendingCheckoutPlanName", planName);
    }
    const isAuthed =
      Boolean(localStorage.getItem("authToken") || localStorage.getItem("token"));
    navigate(isAuthed ? "/dashboard" : "/signup");
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mx-auto w-full max-w-[1440px] overflow-visible px-6"
    >
      <div className="mx-auto flex flex-col items-center text-center">
        <h2 className="font-title text-[36px] font-bold leading-tight text-white sm:text-[44px] md:text-[52px]">
          Pick a plan that&apos;s right for you
        </h2>
        <p className="font-secondary mt-4 text-[17px] text-white sm:text-[18px]">
          Start with our Free plan, no payment required.
        </p>
      </div>

      <div className="mx-auto mt-14 mb-14 grid max-w-[1280px] grid-cols-1 items-center gap-5 overflow-visible py-6 sm:grid-cols-2 sm:py-8 lg:mt-16 lg:grid-cols-4 lg:gap-3 lg:py-10">
        {plans.map((plan, index) => (
          <PricingPlanCardLanding
            key={plan.name}
            name={plan.name}
            subtitle={plan.subtitle}
            price={plan.price}
            priceSuffix={plan.priceSuffix}
            features={plan.features}
            cta={plan.cta}
            elevated={plan.elevated}
            onCta={() => handlePickPlan(plan.planName)}
            motionProps={{
              initial: { opacity: 0, y: 16 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, amount: 0.2 },
              transition: {
                duration: 0.45,
                ease: "easeOut",
                delay: index * 0.05,
              },
            }}
          />
        ))}
      </div>
    </motion.section>
  );
};

export default PricingSection;
